import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, TrendingDown, Package, Wallet, FileText, Users,
  AlertTriangle, ArrowRight, DollarSign, Receipt, Wrench,
  Banknote, PiggyBank,
} from "lucide-react";
import api from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";

/**
 * KPI tile with a vibrant gradient icon + soft radial glow behind it.
 * Each `theme` maps to a distinct color palette for icon gradient + glow.
 */
const THEMES = {
  emerald: { grad: "linear-gradient(135deg, #10B981 0%, #34D399 100%)", shadow: "rgba(16, 185, 129, 0.28)", glow: "rgba(16, 185, 129, 0.08)" },
  rose:    { grad: "linear-gradient(135deg, #F43F5E 0%, #FB7185 100%)", shadow: "rgba(244, 63, 94, 0.28)",  glow: "rgba(244, 63, 94, 0.08)" },
  blue:    { grad: "linear-gradient(135deg, #2563EB 0%, #60A5FA 100%)", shadow: "rgba(37, 99, 235, 0.28)",  glow: "rgba(37, 99, 235, 0.08)" },
  amber:   { grad: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)", shadow: "rgba(245, 158, 11, 0.28)", glow: "rgba(245, 158, 11, 0.08)" },
  violet:  { grad: "linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)", shadow: "rgba(124, 58, 237, 0.28)", glow: "rgba(124, 58, 237, 0.08)" },
  teal:    { grad: "linear-gradient(135deg, #0891B2 0%, #22D3EE 100%)", shadow: "rgba(8, 145, 178, 0.28)",  glow: "rgba(8, 145, 178, 0.08)" },
  orange:  { grad: "linear-gradient(135deg, #FF5500 0%, #FF7A33 100%)", shadow: "rgba(255, 85, 0, 0.30)",   glow: "rgba(255, 85, 0, 0.08)" },
  slate:   { grad: "linear-gradient(135deg, #334155 0%, #64748B 100%)", shadow: "rgba(51, 65, 85, 0.28)",   glow: "rgba(51, 65, 85, 0.08)" },
};

function KpiCard({ label, value, icon: Icon, theme = "orange", trend, testid }) {
  const t = THEMES[theme];
  return (
    <div className="kpi-tile" data-testid={testid}
      style={{ "--tile-glow": t.glow }}>
      <div className="flex items-start justify-between mb-3 relative z-10">
        <div className="kpi-icon" style={{ "--icon-bg": t.grad, "--icon-shadow": t.shadow }}>
          <Icon size={22} strokeWidth={2}/>
        </div>
        {trend != null && (
          <span className={`chip ${trend >= 0 ? "chip-success" : "chip-danger"}`}>
            {trend >= 0 ? <TrendingUp size={11}/> : <TrendingDown size={11}/>}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="text-xs text-[#64748B] font-semibold mb-1 relative z-10">{label}</div>
      <div className="text-2xl font-bold text-[#0F172A] font-mono-data relative z-10">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { active } = useCompany();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!active) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/dashboard/summary");
        setData(data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [active]);

  const cur = active?.base_currency || "INR";
  const s = data || {};

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Dashboard</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A]">
            {active?.name || "Business Overview"}
          </h1>
          <div className="text-sm text-[#64748B] mt-1">
            {active?.country} · {active?.tax_regime?.replace("_", " ")} · Base currency {cur}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/app/job-cards" data-testid="dash-new-job-btn" className="btn-navy w-full sm:w-auto justify-center">
            <Wrench size={14}/> New Job Card
          </Link>
        </div>
      </div>

      {/* KPI Grid — modern 3D-style vibrant icons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Sales (This Month)" value={fmtMoney(s.total_sales, cur)}
                 icon={DollarSign} theme="emerald" testid="kpi-sales" />
        <KpiCard label="Total Expenses" value={fmtMoney(s.total_expenses, cur)}
                 icon={Receipt} theme="rose" testid="kpi-expenses" />
        <KpiCard label="Receivables (AR)" value={fmtMoney(s.total_receivables, cur)}
                 icon={Wallet} theme="blue" testid="kpi-receivables" />
        <KpiCard label="Payables (AP)" value={fmtMoney(s.total_payables, cur)}
                 icon={Banknote} theme="amber" testid="kpi-payables" />
        <KpiCard label="Stock Value" value={fmtMoney(s.stock_value, cur)}
                 icon={Package} theme="violet" testid="kpi-stock" />
        <KpiCard label="Cash Flow (Sales − Expenses)" value={fmtMoney(s.cash_in_hand, cur)}
                 icon={PiggyBank} theme="teal" testid="kpi-cash" />
        <KpiCard label="Invoices Issued" value={s.invoice_count ?? 0}
                 icon={FileText} theme="orange" testid="kpi-invoice-count" />
        <KpiCard label="Low Stock Items" value={s.low_stock_items ?? 0}
                 icon={AlertTriangle} theme="slate" testid="kpi-low-stock" />
      </div>

      {/* Recent Invoices */}
      <div className="card-surface p-5" data-testid="recent-invoices-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-[#0F172A]">Recent Invoices</h2>
          <Link to="/app/invoices" className="text-sm text-[#FF5500] hover:underline flex items-center gap-1 font-semibold">
            View all <ArrowRight size={14}/>
          </Link>
        </div>
        {(s.recent_invoices || []).length === 0 ? (
          <div className="text-center py-8 text-[#64748B] text-sm">
            {loading ? "Loading…" : "No invoices yet. Create your first invoice."}
          </div>
        ) : (
          <table className="table-clean">
            <thead>
              <tr>
                <th>Invoice #</th><th>Party</th><th>Date</th>
                <th className="text-right">Amount</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {s.recent_invoices.map((inv) => (
                <tr key={inv.id} data-testid={`recent-invoice-${inv.id}`}>
                  <td className="font-mono-data font-medium">{inv.number}</td>
                  <td>{inv.party}</td>
                  <td className="font-mono-data text-[#64748B]">{inv.date}</td>
                  <td className="text-right font-mono-data font-semibold">
                    {fmtMoney(inv.total, inv.currency || cur)}
                  </td>
                  <td>
                    <span className={`chip chip-${inv.status === "paid" ? "success" : inv.status === "draft" ? "neutral" : "warn"}`}>
                      {inv.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Link to="/app/job-cards" className="card-surface p-4 hover:border-[#FF5500] transition-colors" data-testid="quick-job-cards">
          <Wrench size={20} className="text-[#FF5500] mb-2"/>
          <div className="font-semibold text-sm">Job Cards</div>
          <div className="text-xs text-[#64748B]">Service work orders</div>
        </Link>
        <Link to="/app/parties?type=customer" className="card-surface p-4 hover:border-[#FF5500] transition-colors" data-testid="quick-customers">
          <Users size={20} className="text-[#2563EB] mb-2"/>
          <div className="font-semibold text-sm">Customers</div>
          <div className="text-xs text-[#64748B]">Manage customer list</div>
        </Link>
        <Link to="/app/items" className="card-surface p-4 hover:border-[#FF5500] transition-colors" data-testid="quick-items">
          <Package size={20} className="text-[#7C3AED] mb-2"/>
          <div className="font-semibold text-sm">Items</div>
          <div className="text-xs text-[#64748B]">Inventory & pricing</div>
        </Link>
        <Link to="/app/expenses" className="card-surface p-4 hover:border-[#FF5500] transition-colors" data-testid="quick-expenses">
          <Receipt size={20} className="text-[#F43F5E] mb-2"/>
          <div className="font-semibold text-sm">Expenses</div>
          <div className="text-xs text-[#64748B]">Track business spend</div>
        </Link>
      </div>
    </div>
  );
}
